import { useDeferredValue, useState } from 'react'
import { parseAsStringLiteral, useQueryState } from 'nuqs'
import { useUrlSearchText } from '@/hooks/use-url-search-text'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { CalendarClock, ChevronDown, Download, Plus, Search } from 'lucide-react'
import { StatusPill } from '@/components/status-pill'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { useQuoteBuilderApi } from '../api/client'
import { quotesQueryOptions } from '../api/queries'
import type { Quote } from '../api/types'
import { activityLabel, formatQuoteDate, formatRelativeTime, quoteDueDateStatus } from '../ui/format'
import { EmptyState, ErrorState, PageHeader } from '../ui/page'
import { QuoteStatusPill } from '../ui/status'
import { Money, PercentValue } from '../ui/values'
import {
  exportQuotes,
  isOpenQuote,
  matchesQuoteSearch,
  matchesStatus,
  quoteProfit,
  quoteRevenue,
  quoteSell,
  quotesThisMonth,
  statusFilters,
} from './data'
import { QuoteEquipment } from './quote-equipment'
import { SearchInput } from '@/components/search-input'

const columns = ['Quote #', 'Quote name', 'Sell total', 'Margin', 'Status', 'Activity'] as const

/** My Quotes (demo `routes/index.tsx`): pipeline summary, status tabs, search, expandable quote rows. */
export function MyQuotesPage() {
  const api = useQuoteBuilderApi()
  const quotesQuery = useQuery(quotesQueryOptions(api))
  const [status, setStatus] = useQueryState(
    'status',
    parseAsStringLiteral(statusFilters.map((tab) => tab.value)).withDefault('all'),
  )
  const [search, setSearch] = useUrlSearchText()
  const deferredSearch = useDeferredValue(search)

  const allQuotes = quotesQuery.data ?? []
  const quotes = allQuotes.filter((quote) => quote.status !== 'archived')
  const visibleQuotes = quotes.filter(
    (quote) => matchesStatus(quote, status) && matchesQuoteSearch(quote, deferredSearch),
  )
  const month = quotesThisMonth(allQuotes)
  const openQuotes = quotes.filter(isOpenQuote)
  const pipelineValue = openQuotes.reduce((sum, quote) => sum + quoteRevenue(quote), 0)
  const revenue = quotes.reduce((sum, quote) => sum + quoteRevenue(quote), 0)
  const margin = revenue ? (quotes.reduce((sum, quote) => sum + quoteProfit(quote), 0) / revenue) * 100 : 0
  const loading = quotesQuery.isLoading

  return (
    <div className="mx-auto w-full max-w-7xl">
      <PageHeader
        actions={
          <>
            <Button disabled={!visibleQuotes.length} onClick={() => exportQuotes(visibleQuotes)} variant="outline">
              <Download aria-hidden="true" />
              Export
            </Button>
            <Button asChild>
              <Link to="/quotes/new">
                <Plus aria-hidden="true" />
                New quote
              </Link>
            </Button>
          </>
        }
        title="My Quotes"
      />

      {quotesQuery.isError ? (
        <div className="rounded-xl border bg-card">
          <ErrorState
            description="My Quotes could not be loaded."
            onRetry={() => void quotesQuery.refetch()}
            title="Quotes unavailable"
          />
        </div>
      ) : (
        <>
          <dl
            aria-label="Quote summary"
            className="mb-5 grid grid-cols-2 overflow-hidden rounded-xl border bg-card lg:grid-cols-4 [&>div]:border-border max-lg:[&>div:nth-child(-n+2)]:border-b max-lg:[&>div:nth-child(odd)]:border-r lg:[&>div:not(:last-child)]:border-r"
          >
            <SummaryItem
              label="Active pipeline"
              loading={loading}
              note={
                <button
                  className="font-medium text-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                  onClick={() => {
                    setStatus('open')
                    setSearch('')
                  }}
                  type="button"
                >
                  {openQuotes.length} open {openQuotes.length === 1 ? 'quote' : 'quotes'}
                </button>
              }
              value={<Money className="font-semibold" value={pipelineValue} />}
            />
            <SummaryItem
              label="Quotes this month"
              loading={loading}
              note={`Created in ${new Date().toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' })}`}
              value={month.created}
            />
            <SummaryItem
              label="Approved this month"
              loading={loading}
              note="By approval date"
              value={<span className="text-emerald-700 dark:text-emerald-400">{month.approved}</span>}
            />
            <SummaryItem
              label="Average margin"
              loading={loading}
              note="Weighted by sell total"
              value={<PercentValue value={margin} />}
            />
          </dl>

          <div aria-label="Quote status" className="flex flex-wrap gap-x-1 gap-y-1 border-b" role="group">
            {statusFilters.map((tab) => (
              <button
                aria-pressed={status === tab.value}
                className={cn(
                  '-mb-px inline-flex min-h-10 items-center gap-1.5 border-b-2 px-2.5 text-sm font-medium outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50',
                  status === tab.value
                    ? 'border-foreground text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
                key={tab.value}
                onClick={() => setStatus(tab.value)}
                type="button"
              >
                {tab.label}{' '}
                {loading ? null : (
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {quotes.filter((quote) => matchesStatus(quote, tab.value)).length}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3">
            <label className="relative w-full sm:max-w-md">
              <span className="sr-only">Search quotes</span>
              <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <SearchInput
                className="bg-card pl-9"
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Quote #, customer, model or manufacturer"
                type="search"
                value={search}
              />
            </label>
            {loading ? null : (
              <span aria-live="polite" className="text-sm text-muted-foreground">
                {visibleQuotes.length} {visibleQuotes.length === 1 ? 'quote' : 'quotes'}
              </span>
            )}
            {search || status !== 'all' ? (
              <Button
                onClick={() => {
                  setSearch('')
                  setStatus('all')
                }}
                size="sm"
                variant="ghost"
              >
                Clear filters
              </Button>
            ) : null}
          </div>

          {loading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton className="h-16 w-full rounded-lg" key={index} />
              ))}
            </div>
          ) : visibleQuotes.length ? (
            <div className="overflow-hidden rounded-lg border bg-card">
              <table className="block w-full border-collapse text-left text-sm lg:table">
                <caption className="sr-only">
                  My quotes. Open a quote by its number, or show its equipment with the quote name.
                </caption>
                <thead className="sr-only lg:not-sr-only lg:table-header-group">
                  <tr className="border-b bg-muted/40">
                    {columns.map((column) => (
                      <th
                        className={cn(
                          'h-10 px-4 font-medium whitespace-nowrap',
                          column === 'Quote #' && 'w-[140px]',
                          (column === 'Sell total' || column === 'Margin') && 'text-right',
                        )}
                        key={column}
                        scope="col"
                      >
                        {column}
                      </th>
                    ))}
                  </tr>
                </thead>
                {visibleQuotes.map((quote) => (
                  <QuoteListRow key={quote.id} quote={quote} />
                ))}
              </table>
            </div>
          ) : (
            <div className="rounded-lg border bg-card">
              {quotes.length ? (
                <EmptyState description="Try another quote number, customer or product." title="No matching quotes" />
              ) : (
                <EmptyState
                  action={
                    <Button asChild>
                      <Link to="/quotes/new">
                        <Plus aria-hidden="true" />
                        New quote
                      </Link>
                    </Button>
                  }
                  title="No quotes yet"
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

function SummaryItem({
  label,
  value,
  note,
  loading,
}: {
  label: string
  value: React.ReactNode
  note: React.ReactNode
  loading: boolean
}) {
  return (
    <div className="min-w-0 p-4">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="mt-1.5 break-words text-xl font-semibold tabular-nums">
        {loading ? <Skeleton className="h-7 w-24" /> : value}
      </dd>
      <dd className="mt-1 text-xs text-muted-foreground">{loading ? <Skeleton className="h-4 w-28" /> : note}</dd>
    </div>
  )
}

/** One quote: number, name and customer, sell, margin, status and activity; expands to its equipment. */
export function QuoteListRow({ quote }: { quote: Quote }) {
  const [expanded, setExpanded] = useState(false)
  const activity = quote.latest_activity
  const activityAt = activity?.created_at ?? quote.updated_at
  const due = quoteDueDateStatus(quote.quote_due_date)
  const cell = 'min-w-0 px-4 py-1.5 align-top lg:py-4'
  const mobileLabel = 'mb-0.5 block text-xs text-muted-foreground lg:hidden'
  return (
    <tbody className={cn('block border-b last:border-b-0 lg:table-row-group', expanded && 'bg-muted/30')}>
      <tr
        className={cn(
          'grid cursor-pointer grid-cols-2 items-start py-2 transition-colors hover:bg-muted/40 lg:table-row lg:py-0',
          expanded && 'bg-muted/50',
        )}
        onClick={() => setExpanded(!expanded)}
      >
        <td className={cn(cell, 'col-span-2')}>
          <Link
            className="inline-block whitespace-nowrap rounded py-1 font-semibold tabular-nums text-primary underline-offset-2 hover:underline lg:py-0"
            onClick={(event) => event.stopPropagation()}
            params={{ quoteId: quote.id, stage: 'equipment' }}
            to="/quotes/$quoteId/workflow/$stage"
          >
            {quote.quote_number}
          </Link>
        </td>
        <td className={cn(cell, 'col-span-2 lg:w-[34%]')}>
          <button
            aria-controls={`items-${quote.id}`}
            aria-expanded={expanded}
            className="flex w-full items-start gap-2 rounded text-left outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
            onClick={(event) => {
              event.stopPropagation()
              setExpanded(!expanded)
            }}
            type="button"
          >
            <ChevronDown
              aria-hidden="true"
              className={cn('mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform', expanded && 'rotate-180')}
            />
            <span className="min-w-0 break-words font-medium leading-5">{quote.title}</span>
          </button>
          <div className="ml-6 mt-0.5 text-xs text-muted-foreground">
            {quote.customer?.uuid ? (
              <Link
                className="break-words underline-offset-2 hover:text-foreground hover:underline"
                onClick={(event) => event.stopPropagation()}
                params={{ groupId: quote.customer.uuid }}
                to="/customers/$groupId"
              >
                {quote.customer.company_name}
              </Link>
            ) : (
              <span className="break-words">{quote.customer?.company_name}</span>
            )}
          </div>
          <p className="ml-6 mt-2 text-xs text-muted-foreground">
            Latest deliver by <span className="font-medium text-foreground">{formatQuoteDate(quote.latest_delivery_date)}</span>
          </p>
        </td>
        <td className={cn(cell, 'whitespace-nowrap lg:text-right')}>
          <span className={mobileLabel}>Sell total</span>
          <Money className="font-semibold" value={quoteSell(quote)} />
        </td>
        <td className={cn(cell, 'whitespace-nowrap lg:text-right')}>
          <span className={mobileLabel}>Margin</span>
          <PercentValue value={quote.totals.margin_percent} />
        </td>
        <td className={cell}>
          <QuoteStatusPill status={quote.status} />
        </td>
        <td className={cn(cell, 'text-xs text-muted-foreground')}>
          <span className="block">{activityLabel(activity?.activity_type)}</span>
          <time className="mt-0.5 block whitespace-nowrap" dateTime={activityAt} title={new Date(activityAt).toLocaleString('en-US')}>
            {formatRelativeTime(activityAt)}
          </time>
          <StatusPill className="mt-2 gap-1" tone={due.tone}>
            <CalendarClock aria-hidden="true" className="size-3" />
            {due.label}
            <time className="font-semibold tabular-nums" dateTime={quote.quote_due_date ?? undefined}>
              {formatQuoteDate(quote.quote_due_date)}
            </time>
          </StatusPill>
        </td>
      </tr>
      {expanded ? (
        <tr className="block lg:table-row">
          <td className="block min-w-0 lg:table-cell" colSpan={columns.length}>
            <QuoteEquipment quote={quote} />
          </td>
        </tr>
      ) : null}
    </tbody>
  )
}
